import React, { lazy, useEffect } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { BREADCRUMB_MAP } from "constants/CONSTANTS";
import PrivateRoute from "./PrivateRoute";
import PermissionRoute from "./PermissionRoute";
import AdminRoute from "./AdminRoute";
import AdminLayout from "app/components/AdminLayout/AdminLayout";
import { PERMISSION_CODES } from "app/hooks/usePermission";
import AiTraining from "app/containers/AITraining";

const Dashboard = lazy(() => import("app/containers/Dashboard/dashboard"));
const News = lazy(() => import("app/containers/News"));
const Feedback = lazy(() => import("app/containers/Feedback"));
const Appointments = lazy(() => import("app/containers/Appointments"));
const Officers = lazy(() => import("app/containers/Officers"));
const PublicService = lazy(() => import("app/containers/Services"));
const AIConversations = lazy(() => import("app/containers/AIConversations"));
const Users = lazy(() => import("app/containers/Users"));
const Roles = lazy(() => import("app/containers/Roles"));
const Settings = lazy(() => import("app/containers/Settings"));
const Areas = lazy(() => import("app/containers/Areas"));
const NotFound = lazy(() => import("app/containers/NotFound/notfound"));
const SignIn = lazy(() => import("app/containers/Signin"));
const CommitmentList = lazy(() => import("app/containers/Commitment/List"));
const CommitmentDetail = lazy(() => import("app/containers/Commitment/Detail"));
const MiniappAccounts = lazy(() => import("app/containers/MiniappAccounts"));
const TaskManagements = lazy(() => import("app/containers/TaskManagements"));
const TaskManagementDetail = lazy(
  () => import("app/containers/TaskManagements/detail"),
);
const FeedbackByDomainPage = lazy(
  () => import("app/containers/FeedbackByDomainPage/FeedbackByDomainPage"),
);
const MiniappUsersDashboardPage = lazy(
  () =>
    import("app/containers/MiniappUsersDashboardPage/MiniappUsersDashboardPage"),
);
const NewsAnalyticsDashboardPage = lazy(
  () =>
    import("app/containers/NewsAnalyticsDashboardPage/NewsAnalyticsDashboardPage"),
);
const Broadcasts = lazy(() => import("app/containers/Broadcasts"));
const EstablishingHouseNumbers = lazy(
  () => import("app/containers/EstablishingHouseNumbers"),
);
const FeedbackByOfficerPage = lazy(
  () => import("app/containers/FeedbackByOfficer"),
);
const ActivityLogsPage = lazy(() => import("app/containers/ActivityLogs"));
const AiUsageStatsPage = lazy(() => import("app/containers/AiUsageStatsPage"));

export default function AppRoutes() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    let pageTitle = BREADCRUMB_MAP[path];
    if (!pageTitle) {
      if (path.startsWith("/task-managements/")) {
        pageTitle = "Chi tiết nhiệm vụ";
      } else if (path.startsWith("/commitment/")) {
        pageTitle = "Chi tiết cam kết";
      } else {
        pageTitle = "Trang chủ";
      }
    }
    document.title = `${pageTitle} | Công an phường Hạc Thành`;
  }, [location]);

  return (
    <Routes>
      <Route path="/signin" element={<SignIn />} />

      <Route element={<PrivateRoute />}>
        <Route element={<AdminLayout />}>
          <Route
            path="/"
            element={
              <PermissionRoute code={PERMISSION_CODES.DASHBOARD}>
                <Dashboard />
              </PermissionRoute>
            }
          />
          <Route
            path="/feedback-domain"
            element={
              <PermissionRoute code={PERMISSION_CODES.FEEDBACK_BY_DOMAIN}>
                <FeedbackByDomainPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/miniapp-users-dashboard"
            element={
              <PermissionRoute code={PERMISSION_CODES.MINIAPP_USERS}>
                <MiniappUsersDashboardPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/news-analytics"
            element={
              <PermissionRoute code={PERMISSION_CODES.NEWS_ANALYTICS}>
                <NewsAnalyticsDashboardPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/news"
            element={
              <PermissionRoute code={PERMISSION_CODES.NEWS}>
                <News />
              </PermissionRoute>
            }
          />
          <Route
            path="/feedback"
            element={
              <PermissionRoute code={PERMISSION_CODES.FEEDBACKS}>
                <Feedback />
              </PermissionRoute>
            }
          />
          <Route
            path="/appointments"
            element={
              <PermissionRoute code={PERMISSION_CODES.APPOINTMENTS}>
                <Appointments />
              </PermissionRoute>
            }
          />
          <Route
            path="/officers"
            element={
              <PermissionRoute code={PERMISSION_CODES.AREA_OFFICERS}>
                <Officers />
              </PermissionRoute>
            }
          />
          <Route
            path="/areas"
            element={
              <PermissionRoute code={PERMISSION_CODES.AREAS}>
                <Areas />
              </PermissionRoute>
            }
          />
          <Route
            path="/services"
            element={
              <PermissionRoute code={PERMISSION_CODES.PUBLIC_SERVICE_LINKS}>
                <PublicService />
              </PermissionRoute>
            }
          />
          <Route
            path="/commitment"
            element={
              <PermissionRoute code={PERMISSION_CODES.COMMITMENTS}>
                <CommitmentList />
              </PermissionRoute>
            }
          />
          <Route
            path="/commitment/new"
            element={
              <PermissionRoute code={PERMISSION_CODES.COMMITMENTS}>
                <CommitmentDetail />
              </PermissionRoute>
            }
          />
          <Route
            path="/commitment/:id"
            element={
              <PermissionRoute code={PERMISSION_CODES.COMMITMENTS}>
                <CommitmentDetail />
              </PermissionRoute>
            }
          />
          <Route
            path="/ai-conversations"
            element={
              <PermissionRoute code={PERMISSION_CODES.CHATS}>
                <AIConversations />
              </PermissionRoute>
            }
          />
          <Route
            path="/ai-training"
            element={
              <PermissionRoute code={PERMISSION_CODES.AI_TRAINING}>
                <AiTraining />
              </PermissionRoute>
            }
          />
          <Route
            path="/users"
            element={
              <PermissionRoute code={PERMISSION_CODES.USERS}>
                <Users />
              </PermissionRoute>
            }
          />
          <Route
            path="/roles"
            element={
              <AdminRoute>
                <Roles />
              </AdminRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PermissionRoute code={PERMISSION_CODES.WARDS}>
                <Settings />
              </PermissionRoute>
            }
          />
          <Route
            path="/miniapp-accounts"
            element={
              <PermissionRoute code={PERMISSION_CODES.MINIAPP_ACCOUNTS}>
                <MiniappAccounts />
              </PermissionRoute>
            }
          />
          <Route
            path="/task-managements"
            element={
              <PermissionRoute code={PERMISSION_CODES.TASK_MANAGERMENTS}>
                <TaskManagements />
              </PermissionRoute>
            }
          />
          <Route
            path="/task-managements/:id"
            element={
              <PermissionRoute code={PERMISSION_CODES.TASK_MANAGERMENTS}>
                <TaskManagementDetail />
              </PermissionRoute>
            }
          />
          <Route
            path="/broadcasts"
            element={
              <PermissionRoute code={PERMISSION_CODES.BROADCASTS}>
                <Broadcasts />
              </PermissionRoute>
            }
          />
          <Route path="/broadcasts/:id" element={<Dashboard />} />
          <Route
            path="/establishing-house-numbers"
            element={
              <PermissionRoute
                code={PERMISSION_CODES.ESTABLISHING_HOUSE_NUMBERS}
              >
                <EstablishingHouseNumbers />
              </PermissionRoute>
            }
          />

          <Route
            path="/officer-feedbacks"
            element={
              <PermissionRoute code={PERMISSION_CODES.FEEDBACK_BY_OFFICER}>
                <FeedbackByOfficerPage />
              </PermissionRoute>
            }
          />

          <Route
            path="/audit-logs"
            element={
              <PermissionRoute code={PERMISSION_CODES.AUDIT_LOGS}>
                <ActivityLogsPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/ai-usage-stats"
            element={
              <PermissionRoute code={PERMISSION_CODES.AI_USAGE_STATS}>
                <AiUsageStatsPage />
              </PermissionRoute>
            }
          />

          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
}
